import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Check as CheckIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import axios from 'axios';
import config from '../config';

const SchoolInvoiceDialog = ({ open, onClose, order, onInvoiceGenerated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [notes, setNotes] = useState('');
  const [generatePDF, setGeneratePDF] = useState(true);

  const handleGenerateInvoice = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get token
      const token = localStorage.getItem('token');
      
      // Call the school invoice generation endpoint
      const response = await axios.post(
        `${config.apiUrl}/api/school-orders/generate-invoice/${order.id}`,
        {
          notes: notes.trim() || null,
          generatePDF: generatePDF
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data?.success) {
        setSuccess(true);
        setInvoiceUrl(response.data.invoiceUrl);
        
        // Call the callback to refresh the parent component
        if (onInvoiceGenerated) {
          onInvoiceGenerated(order.id, response.data);
        }
      } else {
        setError(response.data?.message || 'Failed to generate invoice');
      }
    } catch (err) {
      console.error('Error generating invoice:', err);
      setError(err.response?.data?.message || 'Error generating invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setLoading(false);
    setError(null);
    setSuccess(false);
    setInvoiceUrl(null);
    setNotes('');
    setGeneratePDF(true);
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  if (!order) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6">
            Generate Invoice - Order #{order.id}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            action={
              invoiceUrl && (
                <Button 
                  color="inherit" 
                  size="small"
                  onClick={() => window.open(invoiceUrl, '_blank')}
                  startIcon={<DescriptionIcon />}
                >
                  View Invoice
                </Button>
              )
            }
          >
            Invoice generated successfully! The order status has been updated to "Invoiced".
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                School Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {order.schoolName || order.businessName || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Contact Person
              </Typography>
              <Typography variant="body1">
                {order.contactPerson || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Payment Model
              </Typography>
              <Typography variant="body1">
                {order.paymentModel || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Products Ordered
              </Typography>
              <Typography variant="body1">
                {order.productsOrdered || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Number of Families
              </Typography>
              <Typography variant="body1">
                {order.numberOfFamilies?.toLocaleString() || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Issues Per Term
              </Typography>
              <Typography variant="body1">
                {order.issuesPerTerm || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" color="primary">
                  Total Amount
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {formatCurrency(order.totalPrice)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Invoice Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional notes for the invoice..."
            variant="outlined"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={generatePDF}
                onChange={(e) => setGeneratePDF(e.target.checked)}
                color="primary"
              />
            }
            label="Generate PDF invoice automatically"
          />
        </Box>

        {!success && (
          <Alert severity="info">
            <Typography variant="body2">
              This will generate an invoice for the school order and update the status to "Invoiced". 
              A PDF will be created and the invoice will be accessible via a secure link.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        
        {!success && (
          <Button
            variant="contained"
            onClick={handleGenerateInvoice}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loading ? 'Generating Invoice...' : 'Generate Invoice'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SchoolInvoiceDialog;
